import { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  Button,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';

import {
  DataGrid,
  GridToolbarQuickFilter,
  GridFooter,
  GridFooterContainer,
  GridToolbarContainer,
} from '@mui/x-data-grid';

import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { callApi } from '../../utils/request';
import { useSimulator } from '../../contexts/SimulatorContext';

import currency from 'currency.js';

import Alert from '../../components/Alert';

export default function ListSimulation() {
  const [prospectSimulations, setProspectSimulations] = useState([]);
  const [hasCloture, setHasCloture] = useState(true);
  const navigate = useNavigate();
  const { setResult } = useSimulator();
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [selectedRows, setSelectedRows] = useState(null);
  const [alert, setAlert] = useState(null);

//   const [compactMode, setCompactMode] = useState(localStorage.getItem('compactMode') === 'true');

    // const toggleCompactMode = () => {
    //     const newValue = !compactMode;
    //     setCompactMode(newValue);   
    //     localStorage.setItem('compactMode', newValue);
    // };

  const columns = [
    {
      field: 'created_at', headerName: 'Date de création', width: 200, valueFormatter: (v) => {
        const date = new Date(Number(v * 1000));
        return date.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
      },
    },
    { field: 'created_by', headerName: 'Créateur', width: 200 },
    { field: 'prenom', headerName: 'Prénom', width: 200 },
    { field: 'nom', headerName: 'Nom', width: 200 },
    { field: 'mail', headerName: 'Adresse mail', width: 300 },
    { field: 'phone', headerName: 'Téléphone', width: 200 },
    { field: 'taux_journalier_curr', headerName: 'Taux Journalier', width: 160, valueFormatter: v => currency(v, window.currency_opts).format() },
    { field: 'nb_jours_curr', headerName: 'Jours' },
    { field: 'frais_professionnel_curr', headerName: 'Frais pro.', width: 160, valueFormatter: v => currency(v, window.currency_opts).format() },
    { field: 'frais_gestion_curr', headerName: 'Frais de gestion', width: 120, valueFormatter: v => `${(v * 100).toFixed(2).replace('.', ',')} %` },
    { field: 'source', headerName: 'Source', valueFormatter: v => v ? "Externe" : "Interne" },
    {
      field: 'state',
      headerName: 'État',
      width: 120,
      align: 'center',
      renderCell: (params) => {
        let color, name;
        switch (params.value) {
          case 0:
            color = 'darkorange';
            name = 'Ouverte';
            break;
          case 1:
            color = 'green';
            name = 'Terminée';
            break;
          default:
            color = 'black';
            name = 'Inconnue';
        }

        return <Chip label={name.toUpperCase()} size='small' sx={{color:color, background:'none'}} />;
      }
    }
  ];

  const handleClose = (confirmation) => {
    if (confirmation) {
      deleteProspectSimulation(selectedRows);
    }
    setDeleteConfirm(false);
  };

  function deleteProspectSimulation(id) {
    callApi(`/operateur/deleteSimulation/${id}`, 'DELETE').then((res) => {
      if (res.alert) {
        setAlert(res.alert);
        if (res.alert.variant === 'success') {
          setTimeout(() => setAlert(null), 2500);
        }
      }
    }).catch((err) => {
      console.error('Error during API call:', err);
      setAlert({ variant: 'error', message: "Une erreur s'est produite lors de la suppression de la simulation." });
    });
  }

    // Bar d'outils personnalisée du DataGrid, affichée en bas du tableau,
    // contenant un bouton de suppression de la simulation sélectionnée.
    function FooterToolbar(props) {
        const { sx, ...other } = props ?? {};
        return (
            <GridFooterContainer
                {...other}
                sx={[{ width: '100%', justifyContent: selectedRows ? 'space-between' : 'end' }, sx]}
            >
                {/* Affichage du bouton de suppression uniquement si une ligne est sélectionnée */}
                {selectedRows &&
                <Box display="flex" gap={1}>
                    <Button
                        sx={{ml:2}}
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={() => setDeleteConfirm(true)}
                        children="Supprimer"
                    />
                </Box>}
                <GridFooter sx={{ border: 'none' }} />
            </GridFooterContainer>
        );
    }

    // Bar d'outils personnalisée du DataGrid,
    // contenant un bouton de création de simulation et
    // des éléments de filtrage des simulations.
    function EditToolbar(props) {
        return (
            <GridToolbarContainer {...props}>
            <Grid container alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                <Grid>
                    {/* Bouton de création d'une simulation, redirigeant vers la page de création */}
                    <Button
                        color="warning"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/dashboard/CreateSimulation')}
                        children="Nouvelle simulation"
                    />
                </Grid>
                <Grid>
                    <Grid container alignItems="center" spacing={2}>
                        <Grid item>
                            {/* Champ de recherche rapide */}
                            <GridToolbarQuickFilter
                                variant="outlined"
                                placeholder="Rechercher..."
                                size="small"
                                color='warning'
                            />
                        </Grid>
                        <Grid item>
                            {/* Commutateur pour afficher ou masquer les simulations clôturées */}
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={hasCloture}
                                        color="success"
                                        size='small'
                                        onChange={() => setHasCloture(!hasCloture)}
                                    />
                                }
                                label="Terminées"
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            </GridToolbarContainer>
        );
    }

    // Récupération des simulations de prospects à l'affichage du composant
    // et à chaque changement de l'état d'affichage des simulations clôturées.
    useEffect(() => {
        callApi('/operateur/prospectSimulations', 'GET').then((res) => {
            const filteredSimulations = hasCloture ? res : res.filter(simulation => simulation.state === 0);
            setProspectSimulations(filteredSimulations);
        }).catch((err) => {
            console.error('Error during API call:', err);
            setAlert({ variant: 'error', message: "Une erreur s'est produite lors de la récupération des simulations." });
        });

        setResult(null);
    }, [hasCloture, setResult, alert]);

    return (
    <>
        {/* Affichage des alertes */}
        {alert && <Alert alert={alert}/>}

        {/* Titre de la page */}
        <Typography variant="h4" align='center' children="Liste des simulations" />

        <Box sx={{ width: '90%', margin: '0 auto' }}>
            <DataGrid
                density="compact"
                onRowDoubleClick={(e) => navigate(`/dashboard/Simulateur/${e.id}`)}
                onRowSelectionModelChange={(rowSelect) => setSelectedRows([...rowSelect.ids][0])}
                rows={prospectSimulations}
                columns={columns}
                loading={!prospectSimulations.length}
                initialState={{
                    pagination: {
                        paginationModel: {
                            pageSize: 15,
                        }
                    }
                }}
                pageSizeOptions={[15]}
                showToolbar
                slots={{
                    toolbar: EditToolbar, footer: FooterToolbar
                }}

                sx={{ '--DataGrid-overlayWrapper': '300px' }}
            />
        </Box>

        {/* Dialog de confirmation de suppression d'une simulation */}
        <Dialog
            open={deleteConfirm}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title" children="Suppression d'une simulation" />
            <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    Êtes-vous sûr de bien vouloir supprimer cette simulation ?
                    <br/>
                    Cette action <b>est irréversible</b>.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => handleClose(false)} children="Annuler" />
                <Button onClick={() => handleClose(true)} color="error" children="Supprimer" />
            </DialogActions>
        </Dialog>
    </>
    );
}
